<template>
  <div class="so-overview-container">
    <div class="">
      <form v-on:submit.prevent="submitSearch">
        <div class="d-flex justify-content-start">
          <div>
            <div class="label">Date</div>
            <board-item-date-picker v-model="searchDateRange" :customClass="'boardOverview'" :columns="2" :is-range="true" :mode="'date'" />
          </div>

          <div class="search-items ml-2">
            <div class="input-group">
              <input v-debounce="submitSearch" type="text" class="form-control input-sm" placeholder="Search..." v-model="search" />
              <div class="input-group-append" @click="submitSearch">
                <i class="fa fa-search input-group-text"></i>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <table class="os-table table table-striped table-hover mt-3" v-if="items && items.length">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col">Type</th>
          <th scope="col">Created Date</th>
          <th scope="col">Archived Date</th>

          <th scope="col">Planned</th>
          <th scope="col">In Progress</th>
          <th scope="col">Done</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="so in items" :key="so.id" @click="navigateToOccurrence(so)">
          <th class="os-table-name" scope="col">{{ so.name }}</th>
          <td class="os-table-type" scope="col">{{ so.type }}</td>
          <td class="os-table-date" scope="col">
            <i class="far fa-clock"></i>
            {{ so.createdDate | formatDate }}
          </td>
          <td class="os-table-date" scope="col">
            <i class="far fa-clock"></i>
            {{ so.archivedDate | formatDate }}
          </td>
          <td class="os-table-status" scope="col">
            <div>
              <div style="width: 25px">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.Planned).count }}
              </div>
              <div class="ml-2 badge badge-pill badge-primary sh-prop-s">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.Planned).status }}
              </div>
            </div>
          </td>
          <td class="os-table-status" scope="col">
            <div>
              <div style="width: 25px">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.InProgress).count }}
              </div>
              <div class="ml-2 badge badge-pill badge-warning sh-prop-s">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.InProgress).status }}
              </div>
            </div>
          </td>
          <td class="os-table-status" scope="col">
            <div>
              <div style="width: 25px">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.Done).count }}
              </div>
              <div class="ml-2 badge badge-pill badge-success sh-prop-s">
                {{ getItemsCount(so.boardItemsSummary, itemsStatus.Done).status }}
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!items || !items.length">
      <hr />

      <div class="label justify-content-center d-flex">No Special Occurrences found</div>
    </div>

    <div v-if="loading" class="d-flex align-items-center justify-content-center p-5">
      <loading-spinner></loading-spinner>
    </div>

    <router-view></router-view>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { OverviewModule, OverviewStore } from '@/store/overview';
  import DateRangeDto from '@/types/dateRangeDto';
  import BoardItemDatePicker from '@/components/BoardItem/BoardItemDatePicker.vue';
  import { Watch } from 'vue-property-decorator';
  import { BoardItemStatusDto, BoardItemsSummary, SpecialOccurrenceOverviewDto } from '@/apiclient/apiclient_generated';
  import ShiftOverview from './ShiftOverview.vue';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
  import SpecialOccurrencesRequestParams from '@/types/requestParams/soRequestParams';
  import RouteNames from '@/types/routeNames';

  @Component({
    components: {
      BoardItemDatePicker,
      ShiftOverview,
      LoadingSpinner,
    },
  })
  export default class SpecialOccurrencesOverview extends Vue {
    private overviewStore: OverviewModule = OverviewStore;
    private loading = false;
    private itemsStatus = BoardItemStatusDto;

    private searchDateRange: DateRangeDto = new DateRangeDto();
    private search = '';

    async created() {
      const filters = this.overviewStore.soFilters;

      if (filters && (filters.searchDateRange || filters.search)) {
        this.searchDateRange = filters.searchDateRange;
        this.search = filters.search;
      }

      await this.submitSearch();
    }

    private get items() {
      return this.overviewStore.soOverview;
    }

    @Watch('searchDateRange')
    async changeFromDate() {
      await this.submitSearch();
    }

    async submitSearch() {
      this.loading = true;

      this.overviewStore.setSOFilters({
        searchDateRange: this.searchDateRange,
        search: this.search,
      });

      await this.overviewStore.loadSpecialOccurrencesOverview(this.requestParams);

      this.loading = false;
    }

    private getItemsCount(itemsSummary: BoardItemsSummary[], status: BoardItemStatusDto) {
      const data = itemsSummary.find((_) => _.status == status);

      return data || { count: 0, status };
    }

    navigateToOccurrence(so: SpecialOccurrenceOverviewDto) {
      this.$router.push({
        name: RouteNames.specialOccurrence.root,
        params: { occId: so.id },
      });
    }

    get requestParams(): SpecialOccurrencesRequestParams {
      return {
        orderByDesc: true,
        dateRangeFrom: this.searchDateRange.start,
        dateRangeTo: this.searchDateRange.end,
        searchString: this.search,
      } as SpecialOccurrencesRequestParams;
    }
  }
</script>

<style scoped lang="scss">
  .search-items {
    padding-top: 27px;
    i {
      padding-top: 10px;
    }
    i:hover {
      cursor: pointer;
    }
  }

  .sh-prop-s {
    width: 75px;
    padding-top: 5px;
  }

  .so-overview-container {
    height: 70vh;
    padding: 4px;
    overflow-y: auto;
  }

  .so-overview-container::-webkit-scrollbar {
    width: 8px;
  }

  .so-overview-container::-webkit-scrollbar-track {
    background: #ddd;
    border-radius: 5px;
  }

  .so-overview-container::-webkit-scrollbar-thumb {
    background: #999;
    border: 2px solid #ddd;
    border-radius: 5px;
  }

  .table-hover tbody tr:hover td,
  .table-hover tbody tr:hover th {
    transition: background-color ease 0.3s, margin-top ease 0.3s;
    background-color: #e0edf1;
    cursor: pointer;
  }

  .os-table {
    &-name {
      width: 250px;
    }
    &-type {
      width: 60px;
    }
    &-date {
      width: 250px;
    }
    &-status div:first-child {
      display: flex;
      justify-content: flex-start;
    }
    &-status {
      width: 100px;
    }
  }
</style>
